/**
 * Shell - Universal Layout Primitive
 *
 * Replaces: GeneralLayout, AppShell, LayoutFrame
 *
 * Single configurable component for all layout needs.
 * Use mode prop to switch between patterns.
 *
 * @example Split mode (chat, store):
 * ```tsx
 * <Shell mode="split" left={<Sidebar />} right={<Content />} />
 * ```
 *
 * @example Full shell mode (admin, developer):
 * ```tsx
 * <Shell
 *   mode="full"
 *   sidebar={{ items: navItems, collapsible: true }}
 *   header={<AppHeader />}
 * >
 *   <Routes />
 * </Shell>
 * ```
 *
 * @example Simple mode (single content):
 * ```tsx
 * <Shell mode="simple">
 *   <PageContent />
 * </Shell>
 * ```
 */

import type { ReactNode } from 'react';
import './Shell.css';

export interface ShellProps {
  /**
   * Layout mode
   * - split: Two-panel layout (sidebar + content)
   * - simple: Single content area
   * - full: Full app shell with navigation (future)
   */
  mode?: 'split' | 'simple' | 'full';

  /**
   * Left panel content (split mode)
   */
  left?: ReactNode;

  /**
   * Right panel content (split mode)
   */
  right?: ReactNode;

  /**
   * Main content (simple/full modes)
   */
  children?: ReactNode;

  /**
   * Custom class names
   */
  className?: string;
  containerClassName?: string;
  leftClassName?: string;
  rightClassName?: string;

  /**
   * Mobile responsive states
   */
  leftHidden?: boolean;
  rightActive?: boolean;

  /**
   * Header content (optional)
   */
  header?: ReactNode;

  /**
   * Footer content (optional)
   */
  footer?: ReactNode;
}

/**
 * Shell component - universal layout primitive
 */
export function Shell({
  mode = 'split',
  left,
  right,
  children,
  className,
  containerClassName,
  leftClassName,
  rightClassName,
  leftHidden = false,
  rightActive = false,
  header,
  footer,
}: ShellProps) {
  // Split mode: Two-panel layout (like GeneralLayout)
  if (mode === 'split') {
    const layoutClassName = ['shell', 'shell--split', className ?? '']
      .filter(Boolean)
      .join(' ');
    const containerClasses = ['shell__container', containerClassName ?? '']
      .filter(Boolean)
      .join(' ');
    const leftClasses = [
      'shell__panel',
      'shell__panel--primary',
      leftHidden ? 'shell__panel--hidden-mobile' : '',
      leftClassName ?? '',
    ]
      .filter(Boolean)
      .join(' ');
    const rightClasses = [
      'shell__panel',
      'shell__panel--secondary',
      rightActive ? 'shell__panel--active' : '',
      rightClassName ?? '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={layoutClassName}>
        {header && <div className="shell__header">{header}</div>}
        <div className={containerClasses}>
          <aside className={leftClasses}>{left}</aside>
          <div className={rightClasses}>{right}</div>
        </div>
        {footer && <div className="shell__footer">{footer}</div>}
      </div>
    );
  }

  // Simple mode: Single content area (like LayoutFrame)
  if (mode === 'simple') {
    return (
      <div className={['shell', 'shell--simple', className ?? ''].filter(Boolean).join(' ')}>
        {header && <div className="shell__header">{header}</div>}
        <div className="shell__content">{children}</div>
        {footer && <div className="shell__footer">{footer}</div>}
      </div>
    );
  }

  // Full mode: Complex app shell (future - for now, same as simple)
  // TODO: Implement full shell mode with sidebar navigation when needed
  return (
    <div className={['shell', 'shell--full', className ?? ''].filter(Boolean).join(' ')}>
      {header && <div className="shell__header">{header}</div>}
      <div className="shell__content">{children}</div>
      {footer && <div className="shell__footer">{footer}</div>}
    </div>
  );
}

export default Shell;
